import { useRef } from "react";
import { motion, useInView } from "motion/react";
import { Check, X } from "lucide-react";
import { useLanguage } from "../../i18n/LanguageContext";
import { translations } from "../../i18n/translations";

const silkEase = [0.22, 1, 0.36, 1] as const;

const sectionVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: silkEase } },
};

const YES = "#4ADE80";
const NO = "#F87171";

interface ColumnProps {
  label: string;
  items: string[];
  color: string;
  Icon: typeof Check;
}

function Column({ label, items, color, Icon }: ColumnProps) {
  return (
    <motion.div
      className="rounded-2xl p-7"
      style={{
        border: "1px solid var(--border)",
        background: "var(--surface-1)",
        boxShadow: `inset 0 1px 0 0 ${color}1F`,
      }}
      variants={itemVariants}
    >
      <p
        className="text-sm uppercase mb-6"
        style={{ color, fontFamily: "var(--font-mono)", letterSpacing: "0.10em" }}
      >
        {label}
      </p>
      <ul className="flex flex-col gap-4">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-3">
            <span
              className="w-6 h-6 rounded-md flex items-center justify-center shrink-0 mt-0.5"
              style={{ background: `${color}1A`, color }}
            >
              <Icon className="w-3.5 h-3.5" strokeWidth={2.5} />
            </span>
            <span
              className="text-base leading-snug"
              style={{ color: "var(--foreground-muted)", fontFamily: "var(--font-body)" }}
            >
              {item}
            </span>
          </li>
        ))}
      </ul>
    </motion.div>
  );
}

export function Audience() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.15 });

  const { language } = useLanguage();
  const t = translations[language].audience;

  return (
    <section className="py-24 px-6">
      <motion.div
        ref={sectionRef}
        className="max-w-4xl mx-auto"
        variants={sectionVariants}
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
      >
        <motion.h2
          className="text-3xl md:text-4xl mb-12 text-center"
          style={{ color: "var(--foreground)", fontFamily: "var(--font-display)" }}
          variants={itemVariants}
        >
          {t.heading}
        </motion.h2>

        <div className="grid md:grid-cols-2 gap-6">
          <Column label={t.yesLabel} items={t.yes} color={YES} Icon={Check} />
          <Column label={t.noLabel} items={t.no} color={NO} Icon={X} />
        </div>
      </motion.div>
    </section>
  );
}
